import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Browser-side predicates for the misconfigured-Supabase-keys error classes
 * (BAL-169 / BAL-171). `beforeSend` in `sentry.client.config.ts` drops both
 * outside production.
 */

/**
 * Detect ZodErrors thrown from getSupabaseClientKeys (the most common
 * symptom of a misconfigured preview without NEXT_PUBLIC_SUPABASE_*).
 */
export function isMissingSupabaseKeysError(
  error: unknown,
  eventErrorValue: string,
): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const combined = `${message}\n${eventErrorValue}`.toLowerCase();
  return (
    combined.includes('next_public_supabase_url') ||
    combined.includes('next_public_supabase_public_key') ||
    combined.includes('next_public_supabase_anon_key')
  );
}

/**
 * Detect the TypeError that getSupabaseClientKeys's OWN catch block used
 * to throw by assigning `err.message` on a ZodError, whose `message` is
 * a getter with no setter in zod v3. Earlier versions of this comment
 * blamed Next.js's app-page runtime and a "frozen" ZodError; both were
 * wrong (see the server config for the evidence, BALLEE-J1 2026-09-04).
 * Fixed in 9d29f582f4, so this only matches deploys older than that
 * commit. Same root cause as BAL-169 but a separate Sentry issue
 * (BAL-171 / BALLEE-BV). The browser client runs the same helper, so the
 * TypeError also fired client-side (BALLEE-J9, Safari wording
 * "Attempted to assign to readonly property."), not only via an error
 * boundary replay. The regex below matches V8's wording only, so the
 * Safari form was never filtered here, which is how BALLEE-J9 reached
 * Sentry. Left as is: after 9d29f582f4 neither wording can occur.
 */
export function isPreviewSupabaseKeysTypeError(
  event: ErrorEvent,
  eventErrorValue: string,
): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  if (exception.type !== 'TypeError') return false;
  if (!/cannot set property message of/i.test(eventErrorValue)) return false;
  const frames = exception.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const fn = frame.function ?? '';
    const file = frame.filename ?? '';
    return (
      fn.includes('getSupabaseClientKeys') ||
      file.includes('get-supabase-client-keys')
    );
  });
}
